/**
 * Report creation form and download trigger.
 */

import { useState, type FormEvent } from 'react';
import type { ReportType, ReportCreateRequest } from '@/types/report';
import type { Scan } from '@/types/scan';
import { coreApiMode } from '@/config/deployment';

interface ReportDownloadProps {
  scans: Scan[];
  onSubmit: (payload: ReportCreateRequest) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  hasMoreScans?:boolean;
  onLoadMoreScans?:()=>void;
  isLoadingMoreScans?:boolean;
}

const reportTypes: { value: ReportType; label: string; description: string }[] = [
  { value: 'executive_summary', label: 'Executive Summary', description: 'High-level overview for management' },
  { value: 'full_technical', label: 'Full Technical', description: 'Detailed technical findings for engineers' },
  { value: 'compliance', label: 'Compliance Summary', description: 'Finding summary; readiness assessments are planned' },
  { value: 'delta', label: 'Delta Report', description: coreApiMode?'Compare two immutable scan snapshots':'Legacy summary template' },
];

export default function ReportDownload({ scans, onSubmit, onCancel, isSubmitting,hasMoreScans,onLoadMoreScans,isLoadingMoreScans }: ReportDownloadProps) {
  const [name, setName] = useState('');
  const [reportType, setReportType] = useState<ReportType>('executive_summary');
  const [scanId, setScanId] = useState('');
  const [baselineId,setBaselineId]=useState('');
  const [error, setError] = useState('');

  const completedScans = scans.filter((s) => s.status === 'completed');
  const isDelta=coreApiMode&&reportType==='delta';
  const choices=isDelta?completedScans.filter(scan=>scan.snapshot_status==='complete'):completedScans;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!name.trim()) { setError('Report name is required.'); return; }
    if(isDelta&&(!scanId||!baselineId)){setError('Choose current and baseline scans for the delta report.');return;}
    if(isDelta&&scanId===baselineId){setError('Choose distinct baseline and current scans.');return;}

    onSubmit({
      name: name.trim(),
      report_type: reportType,
      scan_id: scanId || undefined,
      ...(isDelta?{parameters:{baseline_scan_id:baselineId}}:{}),
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div>
        <label htmlFor="report-name" className="block text-sm font-medium text-gray-700">Report Name</label>
        <input
          id="report-name"
          className="input-field mt-1"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Q1 2026 Security Assessment"
        />
      </div>

      <div>
        <label htmlFor="report-type" className="block text-sm font-medium text-gray-700">Report Type</label>
        <select
          id="report-type"
          className="input-field mt-1"
          value={reportType}
          onChange={(e) => setReportType(e.target.value as ReportType)}
        >
          {reportTypes.map((rt) => (
            <option key={rt.value} value={rt.value}>{rt.label} — {rt.description}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="report-scan" className="block text-sm font-medium text-gray-700">{isDelta?'Current scan':'Based on Scan (optional)'}</label>
        <select
          id="report-scan"
          className="input-field mt-1"
          value={scanId}
          onChange={(e) => setScanId(e.target.value)}
        >
          <option value="">{isDelta?'Select current scan':'All findings'}</option>
          {choices.map((scan) => (
            <option key={scan.id} value={scan.id}>{scan.name}</option>
          ))}
        </select>
      </div>

      {isDelta&&<div><label htmlFor="report-baseline" className="block text-sm font-medium text-gray-700">Baseline scan</label>
        <select id="report-baseline" className="input-field mt-1" value={baselineId} onChange={event=>setBaselineId(event.target.value)}>
          <option value="">Select baseline scan</option>
          {choices.filter(scan=>scan.id!==scanId).map(scan=><option key={scan.id} value={scan.id}>{scan.name}</option>)}
        </select>
        <p className="mt-2 text-xs text-gray-500">Only completed scans with immutable observations are eligible. Changed or missing coverage is shown as not retested.</p>
        {choices.length<2&&<p className="mt-2 text-sm text-gray-600">Run two new scans to establish a comparable baseline and current snapshot.</p>}
      </div>}
      {hasMoreScans&&<button type="button" className="btn-secondary" onClick={onLoadMoreScans} disabled={isLoadingMoreScans}>{isLoadingMoreScans?'Loading...':'Load older completed scans'}</button>}

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
        <button type="submit" disabled={isSubmitting} className="btn-primary">
          {isSubmitting ? 'Generating...' : 'Generate Report'}
        </button>
      </div>
    </form>
  );
}
